import { BookingRecoveryStorageError } from "../../../../../lib/booking-recovery-storage";
import { useEffect, useEffectEvent, useRef, useCallback, useReducer } from "react";
import type { BookingReviewResponse } from "@ascleo/frontdesk-contracts/booking-questionnaire/runtime";
import { submitOrRecoverBooking, BookingSubmissionClosed, hasRetainedBookingIntent } from "../../../../../lib/booking-submission-recovery";
import { errorService } from "@ascleo/client/services/error.service";
import { useForm, useStore } from "@tanstack/react-form";
import { Schema } from "effect";
import { useTranslation } from "react-i18next";
import {
  Accessibility,
  CalendarCheck,
  CalendarClock,
  Check,
  Languages,
  MessageSquareText,
  Pencil,
  RotateCw,
  ShieldCheck,
  Stethoscope,
  UserRound,
  Users,
  type LucideIcon,
} from "lucide-react";
import { Button } from "@ascleo/ui/components/ui/button";
import { cn } from "@ascleo/ui/lib/utils";
import {
  formatUtcInstantForLocale,
  parseUtcInstant,
  systemClock,
} from "@ascleo/types/date-time-validity";
import {
  useSchedulerSend,
  useSchedulerActor,
  useBookingQuestionnaireClient,
  useSchedulerSelector,
} from "../context";
import { schedulerSelectors } from "../selectors";
import {
  SchedulerConfirmationSchema,
  type SchedulerConfirmationData,
} from "../schema";
import { docCirrusRpcClient } from "../../../../../lib/rpc/doc-cirrus.client";
import { useAudio } from "../../../../../lib/use-audio";
import { frontdeskDiagnosticLogger } from "../../../../../lib/diagnostic-logger";
import { KioskStep, KioskStepColumns } from "../../../../../components/kiosk-step";
import { KioskTextareaField } from "../../../../../components/kiosk-field";
import { KioskStatePanel } from "../../../../../components/kiosk-state-panel";
import { useStepPrimaryAction } from "../../../context";
import { getFormErrorMessage } from "../../../../../lib/forms/form-errors";
import { useAppointmentTypeName } from "./appointment-type-name";
import type { Insurance } from "../machine";

/** Preset notes so nobody has to type sensitive details on a public screen (KO-78). */
const NOTE_PRESETS: ReadonlyArray<{ id: string; icon: LucideIcon; key: string }> = [
  { id: "wheelchair", icon: Accessibility, key: "self-service.scheduler.confirmation.note-wheelchair" },
  { id: "companion", icon: Users, key: "self-service.scheduler.confirmation.note-companion" },
  { id: "interpreter", icon: Languages, key: "self-service.scheduler.confirmation.note-interpreter" },
];

const INSURANCE_SUMMARY_KEYS: Record<Insurance, string> = {
  GKV: "self-service.scheduler.confirmation.insurance-GKV",
  PKV: "self-service.scheduler.confirmation.insurance-PKV",
  SELFPAY: "self-service.scheduler.confirmation.insurance-SELFPAY",
};

function toggleNoteLine(text: string, line: string): string {
  const lines = text.split("\n").map((entry) => entry.trim()).filter((entry) => entry !== "");
  return lines.includes(line)
    ? lines.filter((entry) => entry !== line).join("\n")
    : [...lines, line].join("\n");
}

type ReviewState = { readonly kind: "loading" } | { readonly kind: "error"; readonly key: string } | { readonly kind: "ready"; readonly key: string; readonly review: BookingReviewResponse };

/**
 * Renders the scheduler booking confirmation step with optional patient notes.
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * <ConfirmationStep />
 * ```
 */
export function ConfirmationStep() {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language ?? "de";
  const send = useSchedulerSend();
  const actor = useSchedulerActor();
  const questionnaireClient = useBookingQuestionnaireClient();
  const operationGeneration = useRef(0);
  const invalidateSubmission = useCallback(() => { operationGeneration.current += 1; }, []);
  useEffect(function ownConfirmationSubmissionLifetime() {
    return function invalidateUnmountedConfirmationSubmission() { invalidateSubmission(); };
  }, [invalidateSubmission]);
  const patient = useSchedulerSelector(schedulerSelectors.patient);
  const appointmentTypeId = useSchedulerSelector(
    schedulerSelectors.appointmentTypeId,
  );
  const capturedTypeName = useSchedulerSelector(schedulerSelectors.appointmentTypeName);
  const appointmentTypeName = useAppointmentTypeName(appointmentTypeId, capturedTypeName);
  const slotId = useSchedulerSelector(schedulerSelectors.slotId);
  const slotCalendarId = useSchedulerSelector(schedulerSelectors.slotCalendarId);
  const slotStart = useSchedulerSelector((s) => s.context.slotStart);
  const slotEnd = useSchedulerSelector((s) => s.context.slotEnd);
  const appointmentDescription = useSchedulerSelector(
    schedulerSelectors.appointmentDescription,
  );
  const questionnaireLanguage = useSchedulerSelector((snapshot) => snapshot.context.questionnaireLanguage);
  const [reviewState, setReviewState] = useReducer((_state: ReviewState, next: ReviewState) => next, { kind: "loading" });
  const [reviewAttempt, retryReview] = useReducer((attempt: number) => attempt + 1, 0);
  const reviewKey = JSON.stringify([patient?.id, appointmentTypeId, slotCalendarId, slotStart, slotEnd, appointmentDescription ?? "", questionnaireLanguage, reviewAttempt]);
  const reviewReady = reviewState.kind === "ready" && reviewState.key === reviewKey;
  let retainedIntent = false;
  try { retainedIntent = patient?.id !== undefined && hasRetainedBookingIntent(globalThis.localStorage, patient.id); }
  catch { /* Submission reports storage failure before any dispatch. */ }
  // Reads the current translation without making `t` a dependency of the review
  // read: a new `t` (language change) must not refetch or reset the review.
  const reportReviewUnavailable = useEffectEvent(function reportReviewUnavailable() {
    // Shown inline; diagnostics never reach the patient (KO-01, KO-02).
    errorService.report(new Error("Booking review unavailable"), { layer: "rpc", source: "frontdesk.scheduler.review", userMessage: t("self-service.scheduler.confirmation.review-unavailable"), suppressToast: true });
  });
  useEffect(function readCurrentBookingReview() {
    let active = true;
    if (questionnaireClient === null || patient?.id === undefined || appointmentTypeId === undefined || slotCalendarId === undefined || slotStart === undefined || slotEnd === undefined) {
      setReviewState({ kind: "error", key: reviewKey });
      return function invalidateMissingReview() { active = false; };
    }
    setReviewState({ kind: "loading" });
    void questionnaireClient.review({ appointmentTypeId, calendarId: slotCalendarId, startTime: slotStart, endTime: slotEnd, description: appointmentDescription === "" ? undefined : appointmentDescription }).then(
      (review) => { if (active) setReviewState({ kind: "ready", key: reviewKey, review }); return undefined; },
      () => {
        if (!active) return undefined;
        reportReviewUnavailable();
        setReviewState({ kind: "error", key: reviewKey });
        return undefined;
      },
    );
    return function invalidatePreviousBookingReview() { active = false; };
  }, [questionnaireClient, patient?.id, appointmentTypeId, slotCalendarId, slotStart, slotEnd, appointmentDescription, reviewKey]);
  // The machine owns "is submitting": entering `booking` makes the confirm
  // control inert at the transition level, so a second tap reaches nothing.
  const isBooking = useSchedulerSelector((snapshot) =>
    snapshot.matches("booking"),
  );

  // Play audio on mount
  useAudio("scheduler-confirmation");

  async function handleConfirm(value: SchedulerConfirmationData): Promise<void> {
    if (!actor.getSnapshot().matches("confirmation")) return;
    if (!retainedIntent && (!reviewReady || value.description !== (appointmentDescription ?? ""))) return;
    if (!patient?.id || !appointmentTypeId || !slotId || !slotCalendarId || !slotStart || !slotEnd) {
      frontdeskDiagnosticLogger.error("Missing required data for booking", {
        hasPatientId: !!patient?.id,
        hasAppointmentTypeId: !!appointmentTypeId,
        hasSlotId: !!slotId,
        hasSlotCalendarId: !!slotCalendarId,
        hasSlotStart: !!slotStart,
        hasSlotEnd: !!slotEnd,
      });
      send({ type: "BOOKING_ERROR" });
      return;
    }

    const description = value.description === "" ? undefined : value.description;
    // Actor transitions are synchronous; a second submit callback must not begin
    // side effects while React is still rendering the previous confirmation.
    const generation = ++operationGeneration.current;
    send({ type: "SUBMIT_BOOKING" });
    try {
      if (questionnaireClient === null) throw new Error("Booking session is required");
      const result = await submitOrRecoverBooking({
        recoveryGeneration: questionnaireClient.recoveryGeneration(),
        patientId: patient.id,
        currentPatientId: () => operationGeneration.current === generation && actor.getSnapshot().matches("booking") ? actor.getSnapshot().context.patient?.id : undefined,
        storage: globalThis.localStorage,
        clock: systemClock,
        create: (receipt) => {
          // Retained status recovery is available even when current create review
          // is unavailable. It must never convert that status check into a write.
          if (!reviewReady || value.description !== (appointmentDescription ?? "")) throw new Error("Current booking review is required");
          return questionnaireClient.submit({
          appointmentTypeId, calendarId: slotCalendarId,
          startTime: slotStart, endTime: slotEnd, description,
          clientSubmissionId: receipt.clientSubmissionId,
          recoveryReceipt: receipt.recoveryReceipt,
          });
        },
        status: docCirrusRpcClient.frontdesk.getBookingStatus,
      });
      if (operationGeneration.current !== generation || !actor.getSnapshot().matches("booking") || actor.getSnapshot().context.patient?.id !== patient.id) return;
      send({ type: "BOOKING_SUCCESS", bookingId: result.scheduleId, startTime: result.startTime, endTime: result.endTime });
    } catch (error) {
      if (operationGeneration.current !== generation || !actor.getSnapshot().matches("booking")) return;
      // Transport/schema failures can include transient answer or note content.
      // The patient sees an outcome state; diagnostics receive no raw payload.
      errorService.report(new Error("Booking submission or recovery failed"), { layer: "rpc", source: "frontdesk.scheduler.booking", userMessage: t("self-service.scheduler.error.pending-info"), suppressToast: true });
      send({ type: "BOOKING_ERROR", outcome: error instanceof BookingSubmissionClosed ? error.reason : error instanceof BookingRecoveryStorageError && ["missing", "expired", "malformed"].includes(error.reason) ? "assistance" : "pending" });
    }
  }

  const defaultValues: SchedulerConfirmationData = { description: appointmentDescription ?? "" };
  const form = useForm({
    defaultValues,
    validators: {
      onChange: Schema.toStandardSchemaV1(SchedulerConfirmationSchema),
    },
    onSubmit: async ({ value }) => {
      await handleConfirm(value);
    },
  });

  const canSubmit = useStore(form.store, (state) => state.canSubmit);
  useStepPrimaryAction({
    label: isBooking
      ? t("self-service.scheduler.confirmation.booking")
      : t(retainedIntent ? "self-service.scheduler.confirmation.check-booking" : "self-service.scheduler.confirmation.book-binding"),
    icon: CalendarCheck,
    tone: "success",
    testId: "scheduler-confirm-booking",
    busy: isBooking,
    disabled: !canSubmit || (!reviewReady && !retainedIntent),
    onClick: () => void form.handleSubmit(),
  });

  const slotDate = slotStart ? parseUtcInstant(slotStart) : null;
  const dateLong = slotDate
    ? formatUtcInstantForLocale(slotDate, locale, {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "";
  const timeLabel = slotDate
    ? formatUtcInstantForLocale(slotDate, locale, {
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";
  const patientName = patient ? `${patient.firstName} ${patient.lastName}`.trim() : "";

  return (
    <KioskStep
      width="default"
      title={t("self-service.scheduler.confirmation.heading")}
      lead={t("self-service.scheduler.confirmation.lead")}
    >
      <KioskStepColumns
        aside={
          <section
            aria-label={t("self-service.scheduler.confirmation.patient-responses")}
            className="flex min-w-0 flex-col gap-3 rounded-3xl border bg-card p-6"
          >
            {reviewReady && reviewState.kind === "ready" ? (
              <BookingReviewRows review={reviewState.review} />
            ) : reviewState.kind === "error" && reviewState.key === reviewKey ? (
              <KioskStatePanel
                tone="error"
                testId="scheduler-review-error"
                title={t("self-service.scheduler.confirmation.review-unavailable")}
                action={{
                  label: t("common.retry"),
                  icon: RotateCw,
                  onClick: () => retryReview(),
                  testId: "scheduler-review-retry",
                  disabled: isBooking,
                }}
              />
            ) : (
              <output className="flex items-center gap-3 text-lg text-muted-foreground">
                <RotateCw aria-hidden className="size-5 animate-spin text-primary" />
                {t("self-service.scheduler.confirmation.review-loading")}
              </output>
            )}
          </section>
        }
      >
        <section
          data-testid="scheduler-confirmation-summary"
          className="flex min-w-0 flex-col gap-1 rounded-3xl bg-primary-soft p-6"
        >
          <dl className="m-0 flex flex-col gap-4">
            <SummaryRow
              icon={Stethoscope}
              label={t("self-service.scheduler.confirmation.type-label")}
              value={appointmentTypeName ?? "—"}
              testId="scheduler-confirmation-type"
            />
            <SummaryRow
              icon={CalendarClock}
              label={t("self-service.scheduler.confirmation.appointment")}
              value={t("self-service.scheduler.confirmation.datetime-value", { date: dateLong, time: timeLabel })}
              testId="scheduler-confirmation-datetime"
            />
            {patient ? (
              <>
                <SummaryRow
                  icon={UserRound}
                  label={t("self-service.scheduler.confirmation.name-label")}
                  value={patientName}
                />
                <SummaryRow
                  icon={ShieldCheck}
                  label={t("self-service.scheduler.confirmation.insurance-label")}
                  value={t(INSURANCE_SUMMARY_KEYS[patient.insurance])}
                />
              </>
            ) : null}
          </dl>
          <Button
            type="button"
            variant="outline"
            size="2xl"
            data-testid="scheduler-confirm-edit-slot-btn"
            onClick={() => send({ type: "BACK" })}
            disabled={isBooking}
            className="mt-3 self-start"
          >
            <Pencil aria-hidden className="size-5" />
            {t("self-service.scheduler.confirmation.change-time")}
          </Button>
        </section>

        <form.Field name="description">
          {(field) => {
            const updateDescription = (nextValue: string) => {
              field.handleChange(nextValue);
              send({ type: "SET_APPOINTMENT_DESCRIPTION", description: nextValue });
            };
            return (
              <section className="flex min-w-0 flex-col gap-4">
                <h2 className="m-0 text-lg font-semibold">
                  {t("self-service.scheduler.confirmation.description-label")}
                </h2>
                <div className="flex flex-wrap gap-3">
                  {NOTE_PRESETS.map(({ id, icon: Icon, key }) => {
                    const line = t(key);
                    const active = field.state.value
                      .split("\n")
                      .map((entry) => entry.trim())
                      .includes(line);
                    return (
                      <button
                        key={id}
                        type="button"
                        aria-pressed={active}
                        data-testid={`frontdesk-scheduler-note-${id}-btn`}
                        disabled={isBooking}
                        onClick={() => updateDescription(toggleNoteLine(field.state.value, line))}
                        className={cn(
                          "flex min-h-14 items-center gap-2 rounded-2xl border-2 px-4 py-2 text-lg font-medium transition-colors outline-none focus-visible:ring-4 focus-visible:ring-ring/45 disabled:opacity-60",
                          active
                            ? "border-primary bg-primary-subtle text-primary-ink"
                            : "border-border bg-card text-foreground hover:bg-foreground-soft",
                        )}
                      >
                        {active ? <Check aria-hidden className="size-5" /> : <Icon aria-hidden className="size-5" />}
                        {line}
                      </button>
                    );
                  })}
                </div>
                <KioskTextareaField
                  id="scheduler-appointment-description"
                  testId="frontdesk-scheduler-description-input"
                  label={t("self-service.scheduler.confirmation.description-free-text")}
                  icon={MessageSquareText}
                  description={t("self-service.scheduler.confirmation.description-hint")}
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={updateDescription}
                  maxLength={500}
                  disabled={isBooking}
                  error={
                    field.state.meta.errors.length > 0
                      ? t(getFormErrorMessage(field.state.meta.errors[0]))
                      : undefined
                  }
                />
              </section>
            );
          }}
        </form.Field>
      </KioskStepColumns>
    </KioskStep>
  );
}

function SummaryRow({
  icon: Icon,
  label,
  value,
  testId,
}: {
  readonly icon: LucideIcon;
  readonly label: string;
  readonly value: string;
  readonly testId?: string;
}) {
  return (
    <div className="flex min-w-0 items-start gap-4">
      <span aria-hidden className="grid size-12 shrink-0 place-items-center rounded-2xl bg-primary-subtle text-primary-ink [&_svg]:size-6">
        <Icon />
      </span>
      <div className="min-w-0">
        <dt className="text-base text-muted-foreground">{label}</dt>
        <dd data-testid={testId} className="m-0 text-xl font-semibold break-words text-foreground">
          {value}
        </dd>
      </div>
    </div>
  );
}

function BookingReviewRows({ review }: { readonly review: BookingReviewResponse }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-3" data-testid="scheduler-review-content">
      {review.rows.length > 0 && (
        <h2 className="m-0 text-lg font-semibold">
          {t("self-service.scheduler.confirmation.patient-responses")}
        </h2>
      )}
      <dl className="m-0 flex flex-col gap-3 text-lg">
        {review.rows.map((row) => (
          <div key={`${row.questionId}:${row.answerId}`} dir="auto" className="flex flex-col gap-0.5">
            <dt className="text-muted-foreground">{row.question}</dt>
            <dd className="m-0 font-medium whitespace-pre-wrap">{row.answer}</dd>
          </div>
        ))}
        {review.note !== undefined && review.note !== "" && (
          <div dir="auto" className="flex flex-col gap-0.5">
            <dt className="text-muted-foreground">{t("self-service.scheduler.confirmation.description-label")}</dt>
            <dd className="m-0 whitespace-pre-wrap">{review.note}</dd>
          </div>
        )}
      </dl>
    </div>
  );
}
